import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Package, TrendingUp, Calculator, ArrowRight, CheckCircle, Zap, BarChart3, Target } from 'lucide-react';

export const Welcome = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    {
      icon: <Package size={32} />,
      title: 'Gestiona tus Productos',
      description: 'Define y organiza todos tus servicios educativos con sus características, precios y costes',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Calculator size={32} />,
      title: 'Calcula Costes',
      description: 'Establece costes variables y fijos para tener un control total de tus finanzas',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Optimiza tu Mix',
      description: 'Encuentra la combinación óptima de servicios que maximiza tu beneficio',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Análisis Detallado',
      description: 'Visualiza métricas de rentabilidad, márgenes y distribución de ingresos',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const benefits = [
    'Maximiza tu beneficio con algoritmos de optimización',
    'Gestiona múltiples productos y servicios',
    'Controla tus costes variables y fijos',
    'Visualiza métricas de rentabilidad en tiempo real',
    'Ajusta manualmente y recalcula al instante'
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"
          style={{
            top: '10%',
            left: '10%',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-purple-300/20 rounded-full blur-3xl animate-pulse"
          style={{
            bottom: '10%',
            right: '10%',
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '2s'
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        {/* Header */}
        <div 
          className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}
          style={{
            transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
          }}
        >
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 rounded-full mb-6 backdrop-blur-sm animate-bounce hover:scale-110 transition-transform duration-300">
            <Sparkles size={48} className="text-white animate-spin-slow" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold text-white mb-4 animate-fade-in" 
            style={{ 
              fontFamily: 'Work Sans',
              textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              animation: 'glow 2s ease-in-out infinite alternate'
            }}
          >
            MixOptimo
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto animate-fade-in-delay">
            Optimiza tu mix de servicios educativos y maximiza tu beneficio
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-2">
            <Button 
              size="lg" 
              className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={() => navigate('/login')}
              data-testid="btn-empezar"
            >
              Empezar Ahora
              <ArrowRight size={20} className="ml-2 animate-bounce-x" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/20 text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300 backdrop-blur-sm"
              onClick={() => navigate('/login')}
            >
              Iniciar Sesión
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className={`p-6 bg-white/95 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{
                animationDelay: `${index * 150}ms`,
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800 group-hover:text-purple-600 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
                {feature.description}
              </p>
            </Card>
          ))}
        </div>

        {/* Benefits Section */}
        <Card className={`p-8 md:p-12 bg-white/95 backdrop-blur-sm mb-12 transform transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 flex items-center justify-center gap-3">
            <Target className="text-purple-600 animate-pulse" size={32} />
            ¿Por qué elegir MixOptimo?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-purple-50 transition-all duration-300 transform hover:translate-x-2 group"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <CheckCircle 
                  size={24} 
                  className="text-green-500 mt-1 flex-shrink-0 group-hover:scale-125 transition-transform duration-300" 
                />
                <p className="text-gray-700 group-hover:text-gray-900 font-medium transition-colors">
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* CTA Section */}
        <div className={`text-center transform transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm inline-block max-w-2xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 relative overflow-hidden group">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-blue-600/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Zap className="text-yellow-500 animate-pulse" size={32} />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                  ¿Listo para optimizar tu negocio?
                </h2>
                <BarChart3 className="text-blue-500 animate-pulse" size={32} />
              </div>
              <p className="text-gray-600 mb-6 text-lg">
                Únete a MixOptimo y comienza a maximizar tu beneficio hoy mismo
              </p>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white hover:from-purple-700 hover:via-blue-700 hover:to-purple-700 text-lg px-8 py-6 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group"
                onClick={() => navigate('/login')}
              >
                <span className="relative z-10 flex items-center">
                  Crear Cuenta Gratis
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes glow {
          from { text-shadow: 0 4px 20px rgba(255,255,255,0.3); }
          to { text-shadow: 0 4px 30px rgba(255,255,255,0.6), 0 0 40px rgba(255,255,255,0.3); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        
        .animate-fade-in-delay {
          animation: fade-in 1s ease-out 0.3s both;
        }
        
        .animate-fade-in-delay-2 {
          animation: fade-in 1s ease-out 0.6s both;
        }
        
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-bounce-x {
          animation: bounce-x 1s ease-in-out infinite;
        }
        
        @keyframes bounce-x {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
};

export default Welcome;

